/*
 * What the For-you feed is told about the viewer.
 *
 * The ranking is only as good as what it is told, and the client is the only
 * thing that knows whether a post was actually looked at. This module is the
 * telling: the queue lives in `./report` (pure, and tested directly), and this
 * file is the plumbing that gives it a session, a clock and a cadence — one
 * batched call to `app.taderok.feed.recordEvents` through the PDS, which
 * forwards it to the AppView (the endpoint is first-party, so no feed-proxy hop
 * is involved).
 *
 * Two rules shape the cadence:
 *
 *  - **Ten seconds for sightings.** An impression is not urgent, and a request
 *    per scroll would be a request per scroll.
 *  - **Half a second for signals.** *Not interested* is a decision about the
 *    ranking, and the ranking should hear it before it builds the next page.
 *
 * The queue is also reachable from outside React (`reportForYouSignal`), because
 * the components that know a post was hidden or shared are not the components
 * that know the feed being drawn is the For-you one. When no For-you feed is on
 * screen nothing is bound and every report is a no-op.
 */
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {AppState, type AppStateStatus} from 'react-native'
import throttle from 'lodash.throttle'

import {useAgent} from '#/state/session'
import {
  type ConsumptionReportWire,
  type FeedSignalKind,
  FOR_YOU_EVENTS_METHOD,
  ForYouReporter,
} from './report'

export {
  DELIVERY_CHUNK,
  FOR_YOU_EVENTS_METHOD,
  ForYouReporter,
  homeEmptyState,
  homeFeedIndex,
  homeFeedPages,
  isForYouFeed,
  MAX_QUEUED_POSTS,
  SURFACE_FEED,
} from './report'
export type {
  ConsumptionReportWire,
  FeedEventBatch,
  FeedSignalKind,
  HomeEmptyState,
  SeenReportWire,
  SignalReportWire,
} from './report'

/** How long a sighting waits to be sent with the others. */
const FLUSH_INTERVAL_MS = 10_000

/** How quickly an explicit signal is sent, because it changes the next page. */
const PROMPT_FLUSH_MS = 500

/**
 * The reporter a screen is currently holding, if any.
 *
 * A module-level binding rather than a context because the signals that know
 * *least* about the feed are the ones that matter most: a post menu inside a
 * thread does not know it is drawing a For-you post, and threading a provider
 * through every menu to tell it would be a large change in service of a small
 * fact. This mirrors `#/state/userActionHistory`, which is also a singleton a
 * screen happens to be feeding.
 */
type ReporterBinding = {
  reporter: ForYouReporter
  flush: () => void
}

let activeBinding: ReporterBinding | null = null

/**
 * Tell the For-you feed a post is not wanted.
 *
 * Safe to call from anywhere: outside a For-you feed it does nothing. The flush
 * is immediate because the ranking's next page is built from what it is told.
 */
export function reportForYouSignal(
  postUri: string,
  kind: FeedSignalKind,
): void {
  const binding = activeBinding
  if (!binding) return
  binding.reporter.reportSignal(postUri, kind)
  binding.flush()
}

/**
 * Report a measurement the player made — progress through a work, a completion.
 *
 * Batched like a sighting: a play is already a sighting the visibility hook has
 * counted, and this only deepens it.
 */
export function reportForYouConsumption(
  report: ConsumptionReportWire,
): void {
  const binding = activeBinding
  if (!binding) return
  binding.reporter.reportConsumption(report)
  binding.flush()
}

export type ForYouFeedReporting = {
  /** Whether reports are being collected at all. */
  enabled: boolean
  /** A row or a set of posts came on screen. */
  markVisible: (uris: readonly string[], position?: number) => void
  /** The same posts left the screen. */
  markHidden: (uris: readonly string[]) => void
  /** A measurement from a player. */
  reportConsumption: (report: ConsumptionReportWire) => void
  /** A deliberate signal. */
  reportSignal: (postUri: string, kind: FeedSignalKind) => void
}

/**
 * Bind a reporter for as long as the For-you feed is the one on screen.
 *
 * `enabled` is the whole gate: when it is false nothing is bound and no request
 * is ever made. Reports are flushed on a throttle, on the app going to the
 * background (a viewer leaving is the most likely moment for reports to be
 * lost), and on unmount.
 */
export function useForYouFeedReporting(enabled: boolean): ForYouFeedReporting {
  const agent = useAgent()
  const reporter = useMemo(() => new ForYouReporter(), [])

  const send = useCallback(() => {
    const batch = reporter.drain()
    if (batch === null) return
    void agent
      .call(FOR_YOU_EVENTS_METHOD, undefined, batch)
      .catch(() => {}) // Best effort: the server upserts, and a retry would cost more than it saves.
  }, [agent, reporter])

  const batchedSend = useMemo(
    () =>
      throttle(send, FLUSH_INTERVAL_MS, {
        leading: false,
        trailing: true,
      }),
    [send],
  )

  const promptSend = useMemo(
    () =>
      throttle(send, PROMPT_FLUSH_MS, {
        leading: true,
        trailing: true,
      }),
    [send],
  )

  useEffect(() => {
    if (!enabled) return
    const binding: ReporterBinding = {reporter, flush: promptSend}
    activeBinding = binding
    return () => {
      if (activeBinding === binding) activeBinding = null
    }
  }, [enabled, reporter, promptSend])

  useEffect(() => {
    if (!enabled) return
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      if (state === 'background') send()
    })
    return () => sub.remove()
  }, [enabled, send])

  useEffect(() => {
    return () => {
      batchedSend.cancel()
      promptSend.cancel()
      send()
    }
  }, [batchedSend, promptSend, send])

  const markVisible = useCallback(
    (uris: readonly string[], position?: number) => {
      if (!enabled) return
      reporter.markVisible(uris, position)
      batchedSend()
    },
    [enabled, reporter, batchedSend],
  )

  const markHidden = useCallback(
    (uris: readonly string[]) => {
      if (!enabled) return
      reporter.markHidden(uris)
      batchedSend()
    },
    [enabled, reporter, batchedSend],
  )

  const reportConsumption = useCallback(
    (report: ConsumptionReportWire) => {
      if (!enabled) return
      reporter.reportConsumption(report)
      batchedSend()
    },
    [enabled, reporter, batchedSend],
  )

  const reportSignal = useCallback(
    (postUri: string, kind: FeedSignalKind) => {
      if (!enabled) return
      reporter.reportSignal(postUri, kind)
      promptSend()
    },
    [enabled, reporter, promptSend],
  )

  return useMemo(
    () => ({
      enabled,
      markVisible,
      markHidden,
      reportConsumption,
      reportSignal,
    }),
    [enabled, markVisible, markHidden, reportConsumption, reportSignal],
  )
}
